import React, { useState, useEffect } from 'react';
import axios from 'axios';

const MondayCalendarScheduler = () => {
    const [boards, setBoards] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [apiToken, setApiToken] = useState('');
    const [selectedBoardId, setSelectedBoardId] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [boardColumns, setBoardColumns] = useState([]);
    const [successMessage, setSuccessMessage] = useState('');
    const [showNewMeetingForm, setShowNewMeetingForm] = useState(false);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
    const [newMeeting, setNewMeeting] = useState({
        name: '',
        date: '',
        time: '',
        duration: 60
    });

    const MONDAY_API_URL = 'https://api.monday.com/v2';

    // Time slots from 8 AM to 6 PM
    const timeSlots = [
        '08:00', '09:00', '10:00', '11:00', '12:00', 
        '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
    ];

    // Load token from localStorage
    useEffect(() => {
        const savedToken = localStorage.getItem('monday_api_token');
        if (savedToken) {
            setApiToken(savedToken);
        }
    }, []);

    const handleTokenChange = (token) => {
        setApiToken(token);
        if (token) {
            localStorage.setItem('monday_api_token', token);
        } else {
            localStorage.removeItem('monday_api_token');
        }
    };

    const clearToken = () => {
        setApiToken('');
        localStorage.removeItem('monday_api_token');
        setBoards([]);
        setItems([]);
        setSelectedBoardId('');
    };

    // Fetch boards
    const fetchBoards = async () => {
        if (!apiToken) {
            setError('Please enter your Monday.com API token');
            return;
        }

        setLoading(true);
        setError(null);

        const query = `
            query {
                boards (limit: 50) {
                    id
                    name
                    description
                    board_kind
                }
            }
        `;

        try {
            const response = await axios.post(
                MONDAY_API_URL,
                { query },
                {
                    headers: {
                        'Authorization': apiToken,
                        'Content-Type': 'application/json',
                        'API-Version': '2024-10'
                    },
                }
            );

            if (response.data.errors) {
                setError(response.data.errors[0].message);
            } else {
                setBoards(response.data.data.boards);
            }
        } catch (err) {
            setError(`Error: ${err.response?.data?.error_message || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Fetch board items with date/time columns
    const fetchBoardItems = async (boardId) => {
        if (!apiToken || !boardId) return;

        setLoading(true);
        setError(null);

        const query = `
            query {
                boards (ids: [${boardId}]) {
                    id
                    name
                    columns {
                        id
                        title
                        type
                    }
                    items_page(limit: 500) {
                        items {
                            id
                            name
                            created_at
                            column_values {
                                id
                                text
                                value
                                type
                            }
                        }
                    }
                }
            }
        `;

        try {
            const response = await axios.post(
                MONDAY_API_URL,
                { query },
                {
                    headers: {
                        'Authorization': apiToken,
                        'Content-Type': 'application/json',
                        'API-Version': '2024-10'
                    },
                }
            );

            if (response.data.errors) {
                setError(response.data.errors[0].message);
            } else {
                const boardData = response.data.data.boards[0];
                const boardItems = boardData?.items_page?.items || [];
                const columns = boardData?.columns || [];
                setItems(boardItems);
                setBoardColumns(columns);
            }
        } catch (err) {
            setError(`Error: ${err.response?.data?.error_message || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleBoardSelect = (boardId) => {
        setSelectedBoardId(boardId);
        fetchBoardItems(boardId);
    };

    // Extract date and time from item
    const getItemDateTime = (item) => {
        const dateColumn = item.column_values.find(col => col.type === 'date' && col.value);
        const timeColumn = item.column_values.find(col => col.type === 'hour' || col.type === 'time-tracking');
        
        let date = null;
        let time = null;

        if (dateColumn && dateColumn.value) {
            try {
                const dateData = JSON.parse(dateColumn.value);
                date = dateData.date;
                time = dateData.time || '09:00:00';
            } catch {
                date = dateColumn.text;
            }
        }

        return { date, time: time ? time.substring(0, 5) : null };
    };

    // Get meetings for selected date
    const getMeetingsForDate = () => {
        return items.filter(item => {
            const { date } = getItemDateTime(item);
            return date === selectedDate;
        });
    };

    // Check if time slot is occupied
    const isTimeSlotOccupied = (timeSlot) => {
        const meetings = getMeetingsForDate();
        return meetings.some(meeting => {
            const { time } = getItemDateTime(meeting);
            return time === timeSlot;
        });
    };

    // Get meeting for time slot
    const getMeetingForTimeSlot = (timeSlot) => {
        const meetings = getMeetingsForDate();
        return meetings.find(meeting => {
            const { time } = getItemDateTime(meeting);
            return time === timeSlot;
        });
    };

    // Create new meeting
    const createMeeting = async () => {
        if (!newMeeting.name || !newMeeting.date || !newMeeting.time) {
            setError('Please fill in all meeting details');
            return;
        }

        setLoading(true);
        setError(null);

        const dateColumn = boardColumns.find(col => col.type === 'date');
        
        if (!dateColumn) {
            setError('No date column found in this board');
            setLoading(false);
            return;
        }

        const columnValues = {
            [dateColumn.id]: {
                date: newMeeting.date,
                time: newMeeting.time + ':00'
            }
        };

        const mutation = `
            mutation {
                create_item (
                    board_id: ${selectedBoardId},
                    item_name: ${JSON.stringify(newMeeting.name)},
                    column_values: ${JSON.stringify(JSON.stringify(columnValues))}
                ) {
                    id
                    name
                }
            }
        `;

        try {
            const response = await axios.post(
                MONDAY_API_URL,
                { query: mutation },
                {
                    headers: {
                        'Authorization': apiToken,
                        'Content-Type': 'application/json',
                        'API-Version': '2024-10'
                    },
                }
            );

            if (response.data.errors) {
                setError(response.data.errors[0].message);
            } else {
                setSuccessMessage('✓ Meeting created successfully!');
                setShowNewMeetingForm(false);
                setNewMeeting({ name: '', date: '', time: '', duration: 60 });
                setSelectedTimeSlot(null);
                await fetchBoardItems(selectedBoardId);
                setTimeout(() => setSuccessMessage(''), 3000);
            }
        } catch (err) {
            setError(`Error creating meeting: ${err.response?.data?.error_message || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Handle time slot click
    const handleTimeSlotClick = (timeSlot) => {
        if (!isTimeSlotOccupied(timeSlot)) {
            setSelectedTimeSlot(timeSlot);
            setNewMeeting({
                ...newMeeting,
                date: selectedDate,
                time: timeSlot
            });
            setShowNewMeetingForm(true);
        }
    };

    return (
        <div style={{ 
            padding: '20px', 
            maxWidth: '1400px', 
            margin: '0 auto',
            height: '100vh',
            overflowY: 'auto'
        }}>
            <h1 style={{ color: '#333', marginBottom: '10px' }}>Monday.com Meeting Scheduler</h1>
            <p style={{ color: '#666', marginBottom: '20px' }}>
                Schedule and manage your meetings
            </p>

            {/* API Token Input */}
            <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <h3 style={{ marginTop: 0 }}>API Token</h3>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                        type="password"
                        value={apiToken}
                        onChange={(e) => handleTokenChange(e.target.value)}
                        placeholder="Enter your Monday.com API token"
                        style={{
                            flex: 1,
                            padding: '12px',
                            fontSize: '14px',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                        }}
                    />
                    <button
                        onClick={fetchBoards}
                        disabled={loading || !apiToken}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: apiToken ? '#0073ea' : '#ccc',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: apiToken ? 'pointer' : 'not-allowed',
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}
                    >
                        {loading ? 'Loading...' : 'Connect'}
                    </button>
                    {apiToken && (
                        <button
                            onClick={clearToken}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            Clear
                        </button>
                    )}
                </div>
            </div>

            {/* Success/Error Messages */}
            {successMessage && (
                <div style={{
                    padding: '15px',
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    border: '1px solid #c3e6cb',
                    fontWeight: 'bold'
                }}>
                    {successMessage}
                </div>
            )}

            {error && (
                <div style={{
                    padding: '15px',
                    backgroundColor: '#fee',
                    color: '#c00',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    border: '1px solid #fcc'
                }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {/* Board Selection */}
            {boards.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                    <h3>Select Calendar Board</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px' }}>
                        {boards.map((board) => (
                            <div
                                key={board.id}
                                onClick={() => handleBoardSelect(board.id)}
                                style={{
                                    padding: '15px',
                                    border: selectedBoardId === board.id ? '2px solid #0073ea' : '1px solid #ddd',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    backgroundColor: selectedBoardId === board.id ? '#e6f2ff' : 'white',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <h4 style={{ margin: '0 0 8px 0' }}>{board.name}</h4>
                                <p style={{ margin: 0, fontSize: '11px', color: '#999' }}>ID: {board.id}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Calendar View */}
            {selectedBoardId && (
                <div style={{ marginTop: '30px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3>Meeting Schedule</h3>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            style={{
                                padding: '10px',
                                fontSize: '14px',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                            }}
                        />
                    </div>

                    <div style={{ 
                        display: 'grid', 
                        gap: '10px',
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                        {timeSlots.map((timeSlot) => {
                            const isOccupied = isTimeSlotOccupied(timeSlot);
                            const meeting = getMeetingForTimeSlot(timeSlot);

                            return (
                                <div
                                    key={timeSlot}
                                    onClick={() => handleTimeSlotClick(timeSlot)}
                                    style={{
                                        padding: '15px',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        cursor: isOccupied ? 'default' : 'pointer',
                                        backgroundColor: isOccupied ? '#ffebee' : '#e8f5e9',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}
                                >
                                    <div>
                                        <strong style={{ fontSize: '16px', color: '#333' }}>{timeSlot}</strong>
                                        {isOccupied && meeting && (
                                            <div style={{ marginTop: '5px', color: '#666', fontSize: '14px' }}>
                                                📅 {meeting.name}
                                            </div>
                                        )}
                                        {!isOccupied && (
                                            <div style={{ marginTop: '5px', color: '#4caf50', fontSize: '13px' }}>
                                                ✓ Available - Click to schedule
                                            </div>
                                        )}
                                    </div>
                                    {isOccupied && (
                                        <span style={{ 
                                            backgroundColor: '#f44336', 
                                            color: 'white', 
                                            padding: '4px 12px', 
                                            borderRadius: '12px',
                                            fontSize: '12px'
                                        }}>
                                            Busy
                                        </span>
                                    )}
                                    {!isOccupied && (
                                        <span style={{ 
                                            backgroundColor: '#4caf50', 
                                            color: 'white', 
                                            padding: '4px 12px', 
                                            borderRadius: '12px',
                                            fontSize: '12px'
                                        }}>
                                            Free
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* New Meeting Form */}
            {showNewMeetingForm && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '8px',
                        maxWidth: '500px',
                        width: '90%'
                    }}>
                        <h3 style={{ marginTop: 0 }}>Schedule New Meeting</h3>
                        
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                Meeting Title *
                            </label>
                            <input
                                type="text"
                                value={newMeeting.name}
                                onChange={(e) => setNewMeeting({ ...newMeeting, name: e.target.value })}
                                placeholder="Enter meeting title"
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    fontSize: '14px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                Date *
                            </label>
                            <input
                                type="date"
                                value={newMeeting.date}
                                onChange={(e) => setNewMeeting({ ...newMeeting, date: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    fontSize: '14px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                Time *
                            </label>
                            <input
                                type="time"
                                value={newMeeting.time}
                                onChange={(e) => setNewMeeting({ ...newMeeting, time: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    fontSize: '14px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd'
                                }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={createMeeting}
                                disabled={loading}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    backgroundColor: '#0073ea',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold'
                                }}
                            >
                                {loading ? 'Creating...' : 'Create Meeting'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowNewMeetingForm(false);
                                    setSelectedTimeSlot(null);
                                }}
                                disabled={loading}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MondayCalendarScheduler;

